import { createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { useTRPC } from "@/trpc";
import { useOrganization } from "@/hooks/use-organization";
import { Permission, useRolePermissions } from "@/hooks/use-role-permissions";
import { FacilityEditDialog } from "@/features/facility/facility-edit-dialog";
import { queryClient } from "@/ajax/queryClient";

type Facility = {
  id: string;
  name: string;
  practiceSetting: {
    id: string;
    displayText: string;
  } | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  isRemote: boolean;
};

const FacilitiesPage = () => {
  const { selectedOrg, selectedOrgBusinessUnit } = useOrganization();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFacility, setEditingFacility] = useState<Facility | null>(null);
  const trpc = useTRPC();
  const { data: currentUser, isLoading: isUserLoading } = useQuery(
    trpc.user.getUser.queryOptions(),
  );
  const { hasPermission } = useRolePermissions(currentUser);
  const canViewFacilities = hasPermission(Permission.ViewJobPosts);
  const canManageFacilities = hasPermission(Permission.ManageJobPosts);

  const { data: facilities = [], isLoading } = useQuery({
    ...trpc.facility.getAll.queryOptions({
      organizationId: selectedOrg?.id || "",
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
    }),
    enabled: canViewFacilities,
  });

  const handleOpenDialog = (facility?: Facility) => {
    if (facility) {
      setEditingFacility(facility);
    } else {
      setEditingFacility(null);
    }
    setIsDialogOpen(true);
  };

  const formatAddress = (facility: Facility) => {
    if (facility.isRemote) {
      return "Remote";
    }

    const parts = [];
    if (facility.address1) {
      parts.push(facility.address1);
    }
    if (facility.address2) {
      parts.push(facility.address2);
    }

    const cityStateZip = [];
    if (facility.city) {
      cityStateZip.push(facility.city);
    }
    if (facility.state) {
      cityStateZip.push(facility.state);
    }
    if (facility.zipCode) {
      cityStateZip.push(facility.zipCode);
    }

    if (cityStateZip.length > 0) {
      parts.push(cityStateZip.join(", "));
    }

    return parts.join(", ") || "-";
  };

  return (
    <div className="container mx-auto py-4 sm:py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-semibold">Facilities</h1>
          {canManageFacilities && (
            <Button onClick={() => handleOpenDialog()}>
              <span className="hidden sm:inline">Add Facility</span>
              <span className="sm:hidden">Add</span>
            </Button>
          )}
        </div>

        {canManageFacilities && isDialogOpen && (
          <FacilityEditDialog
            isOpen={isDialogOpen}
            onOpenChange={setIsDialogOpen}
            facility={editingFacility}
            onSuccess={async () => {
              await queryClient.invalidateQueries(
                trpc.facility.getAll.queryOptions({
                  organizationId: selectedOrg?.id || "",
                  organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                }),
              );
            }}
          />
        )}

        {isUserLoading ? (
          <Card className="p-4">
            <div className="text-center">Loading...</div>
          </Card>
        ) : !canViewFacilities ? (
          <Card className="p-4">
            <div className="text-center">
              You do not have permission to view facilities.
            </div>
          </Card>
        ) : (
          <>
            {/* Mobile Card Layout */}
            <div className="block md:hidden space-y-4">
              {isLoading ? (
                <Card className="p-4">
                  <div className="text-center">Loading...</div>
                </Card>
              ) : facilities.length === 0 ? (
                <Card className="p-4">
                  <div className="text-center">No facilities found</div>
                </Card>
              ) : (
                facilities.map((facility) => (
                  <Card key={facility.id} className="p-4">
                    <div className="space-y-3">
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-semibold text-lg">
                            {facility.name}
                          </h3>
                          <p className="text-sm text-muted-foreground">
                            {facility.practiceSetting?.displayText ||
                              "No type specified"}
                          </p>
                        </div>
                        {canManageFacilities && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleOpenDialog(facility)}
                          >
                            Edit
                          </Button>
                        )}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1">
                          Location
                        </p>
                        <p className="text-sm">{formatAddress(facility)}</p>
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>

            {/* Desktop Table Layout */}
            <Card className="p-0 hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Location</TableHead>
                    {canManageFacilities && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell
                        colSpan={canManageFacilities ? 4 : 3}
                        className="text-center py-4"
                      >
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : facilities.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canManageFacilities ? 4 : 3}
                        className="text-center py-4"
                      >
                        No facilities found
                      </TableCell>
                    </TableRow>
                  ) : (
                    facilities.map((facility) => (
                      <TableRow key={facility.id}>
                        <TableCell>{facility.name}</TableCell>
                        <TableCell>
                          {facility.practiceSetting?.displayText || "-"}
                        </TableCell>
                        <TableCell>
                          {facility.isRemote ? (
                            <div>Remote</div>
                          ) : (
                            <>
                              <div>{facility.address1}</div>
                              {facility.address2 && (
                                <div>{facility.address2}</div>
                              )}
                              <div>
                                {facility.city && <span>{facility.city}</span>}
                                {facility.state && (
                                  <span>, {facility.state}</span>
                                )}
                                {facility.zipCode && (
                                  <span> {facility.zipCode}</span>
                                )}
                              </div>
                            </>
                          )}
                        </TableCell>
                        {canManageFacilities && (
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleOpenDialog(facility)}
                              >
                                Edit
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export const Route = createFileRoute(
  "/_org/org/bu/$organizationBusinessUnitId/facilities/",
)({
  component: FacilitiesPage,
});
